import { useState, useEffect } from "react";
import { OnboardingWelcome } from "./OnboardingWelcome";
import { OnboardingMetricsPage } from "./OnboardingMetricsPage";
import { OnboardingTutorial } from "./OnboardingTutorial";
import { PreAuthMetric } from "@/lib/preAuthMetrics";
import { useTheme } from "@/providers/ThemeProvider";

interface OnboardingProps {
  onComplete: () => void;
  onSkipToAuth: () => void;
  startWithTutorial?: boolean; // For /tour flow: start with tutorial instead of welcome
  onStepChange?: (step: OnboardingStep) => void; // Callback when step changes
}

type OnboardingStep = "welcome" | "generate" | "tutorial" | "tutorial-with-generation";

export const Onboarding = ({ onComplete, onSkipToAuth, startWithTutorial = false, onStepChange }: OnboardingProps) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(startWithTutorial ? "tutorial" : "welcome");

  // Notify parent when step changes
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);
  const [tutorialCompleted, setTutorialCompleted] = useState(false); // Track if tutorial was already shown
  const [metricsGenerated, setMetricsGenerated] = useState(false); // Track if user used generate metrics feature
  const [metricsGenerating, setMetricsGenerating] = useState(false); // Track if metrics are currently generating
  const [userInput, setUserInput] = useState("");
  const [generatedMetrics, setGeneratedMetrics] = useState<PreAuthMetric[]>([]);
  const { theme, setTheme } = useTheme();

  // Force dark mode for onboarding flow
  useEffect(() => {
    const previousTheme = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      // Restore previous theme on unmount
      if (previousTheme !== 'dark') {
        setTheme(previousTheme);
      }
    };
  }, []);

  const handleWelcomeContinue = (input: string) => {
    setUserInput(input);
    setMetricsGenerating(true);
    // If user already saw the tutorial (either from /tour or quick tour), skip showing it again
    if (tutorialCompleted) {
      // Skip tutorial, just show loading and go to metrics page after generation
      setCurrentStep("generate");
    } else {
      // Start tutorial immediately with generation happening in parallel
      setCurrentStep("tutorial-with-generation");
    }
  };

  const handleMetricsGenerated = (metrics: PreAuthMetric[]) => {
    setGeneratedMetrics(metrics);
    setMetricsGenerated(true);
    setMetricsGenerating(false);
  };

  const handleMetricsGenerationError = () => {
    setMetricsGenerating(false);
    setMetricsGenerated(false);
  };

  const handleMetricsConfirm = (metrics: PreAuthMetric[]) => {
    setGeneratedMetrics(metrics);
    setMetricsGenerated(true); // Mark that user has generated metrics
    // Skip review screen and tutorial - go directly to auth
    // The MetricsTour is shown in the metrics page, so we don't need the old tutorial
    onComplete();
  };

  const handleTutorialComplete = () => {
    console.log('[Onboarding] handleTutorialComplete called', { metricsGenerated, generatedMetricsLength: generatedMetrics.length });
    // If metrics finished generating (check both flag and array), show the metrics page for editing
    if (metricsGenerated || generatedMetrics.length > 0) {
      console.log('[Onboarding] Navigating to generate page with metrics:', generatedMetrics.length);
      setCurrentStep("generate");
    } else if (startWithTutorial && !tutorialCompleted) {
      // In tour flow (/tour route): after tutorial, go to welcome page
      setTutorialCompleted(true);
      setCurrentStep("welcome");
    } else if (!startWithTutorial && !tutorialCompleted) {
      // Regular flow quick tour: after tutorial, return to welcome page
      setTutorialCompleted(true);
      setCurrentStep("welcome");
    } else {
      // Tutorial already completed: complete onboarding
      onComplete();
    }
  };

  const handleSkipToTutorial = () => {
    if (tutorialCompleted) {
      // If tutorial already shown (tour flow), go directly to auth
      onComplete();
    } else {
      // Normal flow: show tutorial
      setCurrentStep("tutorial");
    }
  };

  return (
    <>
      {currentStep === "welcome" && (
        <OnboardingWelcome
          onContinue={handleWelcomeContinue}
          onSkip={handleSkipToTutorial}
          onSignIn={onSkipToAuth}
          tutorialCompleted={tutorialCompleted}
          metricsGenerated={metricsGenerated}
        />
      )}
      {currentStep === "generate" && (
        <OnboardingMetricsPage
          userInput={userInput}
          onConfirm={handleMetricsConfirm}
          onBack={() => setCurrentStep("welcome")}
          preGeneratedMetrics={generatedMetrics}
          cameFromTour={tutorialCompleted}
        />
      )}
      {currentStep === "tutorial" && (
        <OnboardingTutorial 
          onComplete={handleTutorialComplete} 
          isTourFlow={startWithTutorial}
          metricsGenerated={metricsGenerated}
        />
      )}
      {currentStep === "tutorial-with-generation" && (
        <OnboardingTutorial 
          onComplete={handleTutorialComplete} 
          isTourFlow={false}
          metricsGenerated={metricsGenerated}
          metricsGenerating={metricsGenerating}
          userInput={userInput}
          onMetricsGenerated={handleMetricsGenerated}
          onMetricsError={handleMetricsGenerationError}
          metricsCount={generatedMetrics.length}
        />
      )}
    </>
  );
};
